import { type FC, type ReactNode, useState } from 'react';

import { FormCargoWeightInput } from '@zen/Cargo';
import FormCargoVariantTiles from '@zen/Cargo/forms/FormCargoVariantTiles';
import { Form, FormButtons, FormInput, FormLabel, FormMultiText, FormNumberInput, FormTextarea } from '@zen/Components/Form';
import FormPackagingTypesSelect from '@zen/Components/Form/FormPackagingTypesSelect/FormPackagingTypesSelect';
import type { FormInstance } from '@zen/Components/Form/types';
import { Button, Card, Headline } from '@zen/DesignSystem';
import { CargoModeEnum } from '@zen/types';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import type { Nullable } from '@zen/utils/typescript';

import { createValidationSchema } from './containerDetailsForm.validation';
import ContainersList from './ContainersList';
import { createTemplateListItems, getInvalidFieldCounts, parseContainers } from './helper';
import { containerFormLabels } from './translations';
import { type Container, emptyContainer } from './types';

interface Props {
  containers: Container[];
  onSubmit: (values: Record<string, Container>) => Promise<IOkOrErrorResult>;
}

const ContainerDetailsForm: FC<Props> = ({ containers, onSubmit }) => {
  const initialValues: Record<string, Container> = parseContainers(containers);

  const [selectedContainerId, setSelectedContainerId] = useState<Nullable<string>>(containers[0].id);

  const handleContainerSelection = (id: string) => {
    setSelectedContainerId(id);
  };

  const renderFormButtons = (form: FormInstance<Record<string, Container>>): ReactNode => {
    const { isSubmitting, setFieldValue } = form;

    return (
      <FormButtons containerClassName="p-4 flex gap-6 justify-end" isSubmitting={isSubmitting} text="Submit">
        <Button
          className="order-first"
          data-testid="cancel-button"
          onClick={() => {
            if (selectedContainerId) {
              setFieldValue(selectedContainerId, {
                ...emptyContainer,
                id: selectedContainerId,
                size: initialValues[selectedContainerId].size,
                grossWeight: { metric: 'KG' }
              });
            }
          }}
          variant="ghost"
        >
          Clear
        </Button>
      </FormButtons>
    );
  };

  const validationSchema = createValidationSchema(initialValues);

  return (
    <Form
      enableReinitialize={true}
      errorClassName="hidden"
      formButtons={() => null}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnMount={true}
      validationSchema={validationSchema}
    >
      {(form) => {
        const { values, errors } = form;

        const missingCounts = getInvalidFieldCounts(values, errors);
        const listItems = createTemplateListItems(Object.values(values));

        return (
          <Card footer={renderFormButtons(form)} variant="compactView">
            <div className="flex gap-6">
              <ContainersList containers={listItems} missingCounts={missingCounts} onContainerSelect={handleContainerSelection} />
              <div className="flex-1 min-w-0 pt-6 pr-6 pb-6">
                <Headline className="mb-6" level={4}>
                  {containerFormLabels.containerGuidelines}
                </Headline>
                <FormLabel label={containerFormLabels.cargoType} />
                <FormCargoVariantTiles cargoMode={CargoModeEnum.LOOSE} name={`${selectedContainerId}.loose.type`} />

                <FormPackagingTypesSelect
                  label={containerFormLabels.packagingTypes}
                  name={`${selectedContainerId}.packagingTypes`}
                />

                <FormNumberInput
                  label={containerFormLabels.quantity}
                  min={0}
                  name={`${selectedContainerId}.loose.quantity`}
                  placeholder="0"
                  step={1}
                />
                <FormCargoWeightInput
                  label={containerFormLabels.grossWeight}
                  min={0}
                  name={`${selectedContainerId}.loose.grossWeight`}
                />
                <FormNumberInput
                  label={containerFormLabels.cbm}
                  min={0}
                  name={`${selectedContainerId}.loose.volume`}
                  placeholder="0.0"
                />

                <FormInput
                  label={containerFormLabels.containerNumber}
                  name={`${selectedContainerId}.number`}
                  onChange={(event, name) => {
                    form.setFieldValue(name, event.target.value.toUpperCase());
                  }}
                  placeholder="XXXX0000000"
                />
                <FormInput label={containerFormLabels.sealNumber} name={`${selectedContainerId}.sealNumber`} />
                <FormTextarea label={containerFormLabels.description} name={`${selectedContainerId}.description`} />
                <FormMultiText
                  label={containerFormLabels.hsCodes}
                  name={`${selectedContainerId}.hsCodes`}
                  placeholder="00000000, 00000001"
                  separateOn={['Tab', 'Comma', 'Space', 'Enter']}
                />
                <FormTextarea label={containerFormLabels.marksAndNumbers} name={`${selectedContainerId}.marksAndNumbers`} />
              </div>
            </div>
          </Card>
        );
      }}
    </Form>
  );
};

export default ContainerDetailsForm;
